import type { MindItem } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { Box } from '@mui/material';
import React, { useContext } from 'react';
import { Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';

import { cardDimensionsMedia, spaces } from '../../../assets/styles/themes/tokens';
import CourseMuiCard from '../../../components/cards/MuiCard/CourseMuiCard/CourseMuiCard';
import { getCourseMuiCardProps } from '../../../components/cards/MuiCard/CourseMuiCard/utils';
import type { Alert } from '../../../components/NotificationAlert/NotificationAlert';
import EmptySearchStateImage from '../../../components/Search/SearchResults/EmptySearchResultsNewUI/EmptySearchStateImage';
import { SearchLoadingStatus } from '../../../reducers/search.selectors';
import { MasterLayoutContext } from '../../../state/MasterLayoutProvider';
import type { Pagination } from '../../../types/search';
import { hasNext } from '../../../utils/pagination';
import useScreenSize from '../../../utils/useScreenSize';
import { useNewUiSearchPagePadding } from '../CourseSearchPage/CourseSearchPage';
import Loader from '../Loader';
import { MyCoursesTabEnum } from './MyCoursesPage';

const MyCoursesPageTab: React.FC<{
	tab: MyCoursesTabEnum;
	items: MindItem<'course'>[];
	loadingState: SearchLoadingStatus;
	pagination: Pagination;
	loadMore: () => void;
	onShowNotification: (alert: Alert) => void;
}> = ({ tab, items, loadingState, pagination, loadMore, onShowNotification }) => {
	const { t } = useTranslation();
	const { mainContentContainer } = useContext(MasterLayoutContext);
	const { isXsScreen } = useScreenSize();
	const paddingX = useNewUiSearchPagePadding();

	const getEmptyResultsText = () => {
		switch (tab) {
			case MyCoursesTabEnum.ALL:
				return t('You have not yet started or completed any courses.');

			case MyCoursesTabEnum.PROGRESS:
				return t('You have not started a course at the moment.');

			case MyCoursesTabEnum.SUCCESS:
				return t('You have not yet started any courses.');

			default:
				return '';
		}
	};

	const emptyResultsText = getEmptyResultsText();

	return (
		<Tab.Pane eventKey={tab} data-testid={`my-courses-${tab}-tab`}>
			<Box
				sx={{
					maxWidth: cardDimensionsMedia.desktop.maxWidth,
					marginX: 'auto',
					paddingY: spaces.lg,
					paddingX,
					minHeight: '100%',
				}}
			>
				{[SearchLoadingStatus.INITIAL, SearchLoadingStatus.NONE].includes(loadingState) ? (
					<Loader />
				) : (
					<>
						{items.length > 0 ? (
							<InfiniteScroll
								loadMore={loadMore}
								hasMore={hasNext(pagination)}
								useWindow={false}
								getScrollParent={() => mainContentContainer}
							>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: isXsScreen ? spaces.sm : spaces.md,
									}}
								>
									{items.map((courseMindItem, index) => (
										<div key={courseMindItem.id}>
											<CourseMuiCard
												{...getCourseMuiCardProps(courseMindItem)}
												index={index}
												onShowNotification={onShowNotification}
											/>
										</div>
									))}
								</Box>
							</InfiniteScroll>
						) : (
							<div>
								<EmptySearchStateImage title={t('No courses available')} subtitle={emptyResultsText} />
							</div>
						)}
						{loadingState === SearchLoadingStatus.PAGINATION && <Loader />}
					</>
				)}
			</Box>
		</Tab.Pane>
	);
};

export default MyCoursesPageTab;
